import { useMemo, type ReactNode } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import useBrains from '@/hooks/useBrains';
import { AIAgentIcon } from '@/modules/aiAgents/icons/AIAgentIcon';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { getComparisonDates } from '@/modules/analytics/utils/getComparisonDates';

import { useAiAgentsVariables } from './useAiAgentsVariables';
import { useComparisonMode } from '../../../hooks/useComparisonMode';
import type { AiAgentMetricKey } from '../aiAgentsInsightsConfig';

export type AiAgentMetricValues = Record<AiAgentMetricKey, number>;

export interface AiAgentRow {
  id: string;
  name: string;
  icon: ReactNode;
  current: AiAgentMetricValues;
  previous: AiAgentMetricValues;
}

interface AiAgentsMetricsRow {
  brain_parent_id: string;
  total_conversations: number | null;
  containment_rate: number | null;
  avg_response_time: number | null;
  avg_csat_brain: number | null;
  avg_user_rating: number | null;
  csat_brain_count: number | null;
  user_rating_count: number | null;
}

const METRICS_FIELDS = `
  brain_parent_id
  total_conversations
  containment_rate
  avg_response_time
  avg_csat_brain
  avg_user_rating
  csat_brain_count
  user_rating_count
`;

const GET_AI_AGENTS_CONVERSATIONS_BY_AGENT = gql`
  query AiAgentsConversationsByAgent(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_conversations_by_agent(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${METRICS_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_CONVERSATIONS_BY_AGENT_COMPARISON = gql`
  query AiAgentsConversationsByAgentComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_conversations_by_agent(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${METRICS_FIELDS}
    }
  }
`;

const round1 = (value: number | null | undefined): number =>
  value == null ? 0 : Number(value.toFixed(1));

const toMetricValues = (row: AiAgentsMetricsRow | undefined): AiAgentMetricValues => ({
  csat: round1(row?.avg_csat_brain),
  containment: Math.round((row?.containment_rate ?? 0) * 100),
  // Backend returns minutes; convert to seconds so consumers can format directly.
  responseTime: (row?.avg_response_time ?? 0) * 60,
  totalConversations: row?.total_conversations ?? 0,
});

export const useAiAgents = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { brains } = useBrains();

  const { variables: currentVariables, skip } = useAiAgentsVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useAiAgentsVariables(comparisonDates);

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useQuery<{ rows: AiAgentsMetricsRow[] }>(GET_AI_AGENTS_CONVERSATIONS_BY_AGENT, {
    variables: currentVariables,
    skip,
  });

  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
  } = useQuery<{ rows: AiAgentsMetricsRow[] }>(GET_AI_AGENTS_CONVERSATIONS_BY_AGENT_COMPARISON, {
    variables: comparisonVariables,
    skip: skip || !isComparing,
  });

  const data = useMemo<AiAgentRow[]>(() => {
    const currentRows = currentData?.rows ?? [];
    const previousByParent = new Map<string, AiAgentsMetricsRow>(
      (comparisonData?.rows ?? []).map((row) => [row.brain_parent_id, row])
    );

    // `useBrains` returns one record per logical agent (the head version);
    // its `brain_id` matches the metrics row's `brain_parent_id`.
    const brainsByParent = new Map((brains ?? []).map((brain) => [brain.brain_id, brain]));

    return currentRows.map((row) => {
      const brain = brainsByParent.get(row.brain_parent_id);
      return {
        id: row.brain_parent_id,
        name: brain?.name ?? row.brain_parent_id,
        icon: <AIAgentIcon type={brain?.brain_type ?? 'support'} />,
        current: toMetricValues(row),
        previous: toMetricValues(previousByParent.get(row.brain_parent_id)),
      };
    });
  }, [currentData?.rows, comparisonData?.rows, brains]);

  return {
    data,
    isLoading: currentLoading || (isComparing && comparisonLoading),
    error: currentError ?? comparisonError ?? null,
  };
};
